import { OrderTypeEnum } from '@libs/api-reference';
import { i18n } from '@libs/i18n';
import { ComputedRef, Ref } from 'vue';

const ORDER_SIDES = {
  buy: 1,
  sell: 2,
} as const;

const ALGORITHM_ORDER_TYPES = [
  OrderTypeEnum.Conditional,
  OrderTypeEnum.Offline,
  OrderTypeEnum.SLTP,
];

const { t } = i18n.global;

const ORDERS_LABEL = t('common.orders');

const ALGORITHM_ORDER_CONDITIONS = {
  baseOnQuantity: 'quantity',
  baseOnTotalValue: 'value',
};

const ORDER_SIDES_LABEL = {
  buy: t('common.buy'),
  sell: t('common.sell'),
};

const LEGACY_ORDER_PENDING_STATUSES = {
  None: 0,
  Pending: 1,
  Action: 2,
  PendingAction: 3,
  Sending: 4,
  Sent: 16,
  PendingCancellation: 129,
  CancelledAndSending: 132,
  CancelledAndSent: 144,
  ModifiedAndSending: 1028,
  ModifiedAndSent: 1040,
  PendingModification: 24576,
};

const NEW_OMS_ORDER_PENDING_STATUSES = {
  InAction: 1,
  Sent: 2,
  CancelInAction: 6,
  PartiallyExecutedAndCancelInAction: 7,
  PartiallyExecutedAndCancelSent: 8,
  CancelSent: 12,
  ModifyInAction: 16,
  ModificationSent: 17,
  AlgorithmPlanning: 10000,
};

const NEW_OMS_ORDER_STATUSES = {
  open: {
    PartiallyExecuted: 5,
    InBook: 13,
    ModifiedAndInBook: 18,
    Stopped: 256,
    PartiallyExecutedAndCancelledByTse: 10,
    PartiallyExecutedAndPartiallyCancelledByTse: 26,
    AlgorithmRequested: 123456789,
    AlgorithmStopped: 10009,
    // Order status in legacy but should be considered in new OMS until IPO is recreated by OMS
    TSEFixSendInTradeEngine: 393222,
    TSEFixEditedByObserver: 393254,
    OrderModificationInBook: 49152,
    ...NEW_OMS_ORDER_PENDING_STATUSES,
  },

  failed: {
    Cancelled: 3,
    Error: 6,
    OrderModified: 23,
    OrderRejected: 14,
    OrderEliminatedByTse: 15,
    RejectedByOms: 21,
    ExecutedAndCancelledByTse: 11,
    SendingOrderFailed: 20,
    Expired: 21,
    PartiallyExecutedAndCancelledByTse: 10,
    PartiallyExecutedAndPartiallyCancelledByTse: 26,
    OrderCancelledByTse: 24,
    PartiallyExecutedAndPartiallyEliminatedAndCancelledByTse: 28,
    PartiallyExecutedAndExpiredAndCancelledByTse: 30,
    PartiallyExecutedAndCancelledAndCancelledByTse: 33,
    PartiallyExecutedAndCancelledAndPartiallyCancelledByTse: 32,
    PartiallyExecutedAndModifiedAndPartiallyCancelledByTse: 34,
    PartiallyExecutedAndModifiedAndCancelledByTse: 35,
    AlgorithmCancelled: 10003,
    AlgorithmExpired: 10005,
    AlgorithmFailed: 10007,
    AlgorithmPartiallyExecutedAndFailed: 10008,
    AlgorithmPartiallyExecutedAndExpired: 10006,
    AlgorithmPartiallyExecutedAndCancelled: 10004,
  },

  done: {
    PartiallyExecuted: 5,
    AlgorithmExecuted: 10001,
    AlgorithmPartiallyExecuted: 10002,
    Executed: 4,
    PartiallyExecutedAndCancelled: 9,
    PartiallyExecutedAndEliminatedByTse: 19,
    PartiallyExecutedAndCancelInAction: 7,
    PartiallyExecutedAndCancelSent: 8,
    PartiallyExecutedAndModified: 22,
    PartiallyExecutedAndPartiallyCancelledByTse: 26,
    ExecutedAndPartiallyCancelledByTse: 25,
    PartiallyExecutedAndPartiallyEliminatedAndPartiallyCancelledByTse: 27,
    PartiallyExecutedAndExpired: 29,
    PartiallyExecutedAndExpiredAndPartiallyCancelledByTse: 31,
    PartiallyExecutedAndCancelledAndPartiallyCancelledByTse: 32,
    PartiallyExecutedAndModifiedAndPartiallyCancelledByTse: 34,
    AlgorithmPartiallyExecutedAndFailed: 10008,
    AlgorithmPartiallyExecutedAndExpired: 10006,
    AlgorithmPartiallyExecutedAndCancelled: 10004,
  },
};

const LEGACY_ORDER_STATUSES = {
  open: {
    ModificationRejectedAndOrderComBackInBook: 98302,
    Confirmed: 32,
    ExecutedAndConfirmed: 2080,
    OrderModificationInBook: 49152,
    Stopped: 256,
    TSEFixSendInTradeEngine: 393222,
    TSEFixEditedByObserver: 393254,
    AlgorithmRequested: 123456789,
    ...LEGACY_ORDER_PENDING_STATUSES,
  },

  failed: {
    Cancelled: 128,
    Eliminated: 512,
    Deleted: 196609,
    DeletedExpirationOrder: 196611,
    TSEFixRemoveTheObserver: 393252,
    Error: 8,
    ErrorInSending: 12,
    Rejected: 64,
    TSEFixTheCustomerIsNotAllowedToBuy: 393224,
    TSEFixTheCustomerIsNotAllowedToBuyInThisBroker: 393226,
    TSEFixTheVolumeOfRequestsIsOffLimits: 393228,
    TSEFixTheRequestMustBeAMultipleOfTheTradingUnit: 393230,
    TSEFixPriceEnteredIsOutsideTheThreshold: 393232,
    TSEFixThePriceRequestedMustBeAMultipleOfTheTick: 393234,
    TSEFixBuyingForTheseInvestorsHaveAlreadyBeenRegistered: 393236,
    TSEFixArrivalTimeHasNotYetStartedAskingForInitialPublicOffering: 393238,
    TSEFixTimeoutAcceptRequests: 393240,
    TSEFixThisSymbolMayNotIncludeRequestsInitialPublicOffering: 393242,
    TSEFixNoRecordWasFoundForTheCancellationOf: 393244,
    TSEFixAccountIDBrokerInTheServerCodeInTheCodeAreNotTheSameTraderID: 393246,
    TSEFixBrokerCodeIsNotCorrect: 393248,
    TSEFixThisInitialReleaseDoesNotApplyToBrokers: 393250,
    ModificationRejected: 196608,
  },

  done: {
    Executed: 2048,
    ExecutedAndRejected: 2112,
    ExecutedAndCancelled: 2176,
    ExecutedAndStopped: 2304,
    ExecutedAndEliminated: 2560,
    ExecutedAndModified: 3072,
    ExecutedAndDeleted: 196610,
    Modified: 1024,
  },
};

const NEW_OMS_ORDERS_HISTORY_CATEGORY = {
  open: '1',
  done: '2',
  failed: '3',
};

const ERRORS_TO_SHOW = {
  Error: 8,
  ErrorInSending: 12,
  Rejected: 64,
  TSEFixRemoveTheObserver: 393252,
  TSEFixTheCustomerIsNotAllowedToBuy: 393224,
  TSEFixTheCustomerIsNotAllowedToBuyInThisBroker: 393226,
  TSEFixTheVolumeOfRequestsIsOffLimits: 393228,
  TSEFixTheRequestMustBeAMultipleOfTheTradingUnit: 393230,
  TSEFixPriceEnteredIsOutsideTheThreshold: 393232,
  TSEFixThePriceRequestedMustBeAMultipleOfTheTick: 393234,
  TSEFixBuyingForTheseInvestorsHaveAlreadyBeenRegistered: 393236,
  TSEFixArrivalTimeHasNotYetStartedAskingForInitialPublicOffering: 393238,
  TSEFixTimeoutAcceptRequests: 393240,
  TSEFixThisSymbolMayNotIncludeRequestsInitialPublicOffering: 393242,
  TSEFixNoRecordWasFoundForTheCancellationOf: 393244,
  TSEFixAccountIDBrokerInTheServerCodeInTheCodeAreNotTheSameTraderID: 393246,
  TSEFixBrokerCodeIsNotCorrect: 393248,
  TSEFixThisInitialReleaseDoesNotApplyToBrokers: 393250,
  ModificationRejected: 196608,
};

const LEGACY_ORDER_STATUSES_GROUPS = {
  done: Object.values(LEGACY_ORDER_STATUSES.done),
  failed: Object.values(LEGACY_ORDER_STATUSES.failed),
  open: Object.values(LEGACY_ORDER_STATUSES.open),
};

const NEW_OMS_ORDER_STATUSES_GROUPS = {
  done: Object.values(NEW_OMS_ORDER_STATUSES.done),
  failed: Object.values(NEW_OMS_ORDER_STATUSES.failed),
  open: Object.values(NEW_OMS_ORDER_STATUSES.open),
};

const STATUS = {
  open: t('orders.status.open'),
  done: t('orders.status.done'),
  edited: t('orders.status.edited'),
  editedAndQueued: t('orders.status.edited_and_queued'),
  executedAndCancelled: t('orders.status.executed_and_cancelled'),
  executedAndConfirmed: t('orders.status.executed_and_confirmed'),
  executedAndEdited: t('orders.status.executed_and_edited'),
  executedAndEliminated: t('orders.status.executedAndEliminated'),
  executedAndRejected: t('orders.status.executedAndRejected'),
  error: t('orders.status.error'),
  queued: t('orders.status.queued'),
  failed: t('orders.status.failed'),
  pending: t('orders.status.pending'),
  deleted: t('orders.status.deleted'),
  deletedExpiration: t('orders.status.deletedExpiration'),
  cancelled: t('orders.status.cancelled'),
  cancelledAndSent: t('orders.status.cancelledAndSent'),
  modifiedAndSent: t('orders.status.modifiedAndSent'),
  readyToExecute: t('orders.status.readyToExecute'),
  requested: t('orders.status.requested'),
  stopped: t('orders.status.stopped'),
} as const;

const LEGACY_ORDER_STATUSES_LABEL = {
  Executed: STATUS.done,
  ExecutedAndConfirmed: STATUS.executedAndConfirmed,
  ExecutedAndRejected: STATUS.executedAndRejected,
  ExecutedAndCancelled: STATUS.executedAndCancelled,
  ExecutedAndStopped: STATUS.done,
  ExecutedAndEliminated: STATUS.executedAndEliminated,
  ExecutedAndModified: STATUS.done,
  ExecutedAndDeleted: STATUS.done,
  Cancelled: STATUS.cancelled,
  Eliminated: STATUS.cancelled,
  Deleted: STATUS.cancelled,
  DeletedExpirationOrder: STATUS.deletedExpiration,
  TSEFixRemoveTheObserver: STATUS.deleted,
  Error: STATUS.error,
  Rejected: STATUS.error,
  ErrorInSending: STATUS.error,
  TSEFixTimeoutAcceptRequests: STATUS.error,
  TSEFixBrokerCodeIsNotCorrect: STATUS.error,
  TSEFixTheCustomerIsNotAllowedToBuy: STATUS.error,
  TSEFixTheVolumeOfRequestsIsOffLimits: STATUS.error,
  TSEFixPriceEnteredIsOutsideTheThreshold: STATUS.error,
  TSEFixNoRecordWasFoundForTheCancellationOf: STATUS.error,
  TSEFixThisInitialReleaseDoesNotApplyToBrokers: STATUS.error,
  TSEFixTheCustomerIsNotAllowedToBuyInThisBroker: STATUS.error,
  TSEFixTheRequestMustBeAMultipleOfTheTradingUnit: STATUS.error,
  TSEFixThePriceRequestedMustBeAMultipleOfTheTick: STATUS.error,
  TSEFixBuyingForTheseInvestorsHaveAlreadyBeenRegistered: STATUS.error,
  TSEFixArrivalTimeHasNotYetStartedAskingForInitialPublicOffering: STATUS.error,
  TSEFixThisSymbolMayNotIncludeRequestsInitialPublicOffering: STATUS.error,
  TSEFixAccountIDBroekrInTheServerCodeInTheCodeAreNotTheSameTraderID:
    STATUS.error,
  Sent: STATUS.pending,
  None: STATUS.pending,
  Action: STATUS.pending,
  Sending: STATUS.pending,
  Pending: STATUS.readyToExecute,
  PendingAction: STATUS.pending,
  ModifiedAndSent: STATUS.modifiedAndSent,
  CancelledAndSent: STATUS.cancelledAndSent,
  ModifiedAndSending: STATUS.pending,
  PendingCancellation: STATUS.pending,
  CancelledAndSending: STATUS.pending,
  PendingModification: STATUS.pending,
  Confirmed: STATUS.queued,
  OrderModificationInBook: STATUS.editedAndQueued,
  Stopped: STATUS.stopped,
  TSEFixSendedInTradeEngine: STATUS.queued,
  TSEFixEditedByObserver: STATUS.queued,
  Modified: STATUS.edited,
  ModifiedAndEdited: STATUS.executedAndEdited,
  ModificationRejectedAndOrderComBackInBook: STATUS.queued,
  AlgorithmRequested: STATUS.requested,
} as const;

const NEW_OMS_ORDER_STATUSES_LABEL = {
  InAction: STATUS.pending,
  Sent: STATUS.pending,
  CancelInAction: STATUS.pending,
  CancelSent: STATUS.cancelledAndSent,
  ModifyInAction: STATUS.pending,
  ModificationSent: STATUS.modifiedAndSent,
  PartiallyExecuted: STATUS.executedAndConfirmed,
  InBook: STATUS.queued,
  ModifiedAndInBook: STATUS.editedAndQueued,
  Stopped: STATUS.stopped,
  AlgorithmRequested: STATUS.requested,
  Cancelled: STATUS.cancelled,
  Error: STATUS.error,
  OrderRejected: STATUS.error,
  OrderEliminatedByTse: STATUS.cancelled,
  RejectedByOms: STATUS.error,
  SendingOrderFailed: STATUS.error,
  Expired: STATUS.deleted,
  OrderCancelledByTse: STATUS.cancelled,
  Executed: STATUS.done,
  PartiallyExecutedAndCancelInAction: STATUS.executedAndCancelled,
  PartiallyExecutedAndCancelSent: STATUS.executedAndCancelled,
  PartiallyExecutedAndCancelled: STATUS.executedAndCancelled,
  PartiallyExecutedAndCancelledByTse: STATUS.executedAndCancelled,
  ExecutedAndCancelledByTse: STATUS.executedAndCancelled,
  PartiallyExecutedAndEliminatedByTse: STATUS.executedAndEliminated,
  PartiallyExecutedAndModified: STATUS.done,
  OrderModified: STATUS.edited,
  ModifiedAndEdited: STATUS.executedAndEdited,
};

const ORDER_FILTERS = {
  open: {
    value: 'open',
    label: STATUS.open,
  },
  done: {
    value: 'done',
    label: STATUS.done,
  },
  failed: {
    value: 'failed',
    label: STATUS.failed,
  },
  all: {
    value: 'all',
    label: t('orders.status.all'),
  },
} as const;

const BASE_ORDER_FORM = {
  quantity: null,
  price: null,
  totalValue: null,
} as const;

const VALIDITY_TYPES = {
  Day: 1,
  GoodTillCancelled: 3,
  GoodTillDate: 2,
} as const;

const VALIDITY_TYPE_LABEL = {
  [VALIDITY_TYPES.Day]: t('orders.valid_until_day'),
  [VALIDITY_TYPES.GoodTillCancelled]: t('orders.valid_until_cancelled'),
  [VALIDITY_TYPES.GoodTillDate]: t('orders.valid_until_date'),
} as const;

const EMPTY_ORDER_MESSAGE = {
  title: {
    open: t('orders.empty_open_order.title'),
    done: t('orders.empty_done_order.title'),
    failed: t('orders.empty_failed_order.title'),
    all: t('orders.empty_today_orders'),
  },
  message: {
    open: t('orders.empty_open_order.message'),
    done: t('orders.empty_done_order.message'),
    failed: t('orders.empty_failed_order.message'),
    all: t('orders.empty_open_order.message'),
  },
} as const;

const IPO_REGISTERED_STATE_TITLE =
  'TSEFixBuyingForTheseInvestorsHaveAlreadyBeenRegistered';

const ORDER_SIDES_TABS = {
  buy: 0,
  sell: 1,
} as const;

const SERVER_CLOCK_STATE = {
  preOpening: ['tse_pre_opening'],
  open: [
    'tse_opening',
    'tse_continuous_auction',
    'tse_final_batch_auction',
    'tse_final_batch_auction',
    'tse_winning_final',
  ],
};

const SERVER_CLOCK_STATE_DETAIL = {
  preOpening: {
    color: 'text-warning-primary',
    title: t('misc.pre-opening'),
    state: 'preOpening',
  },
  open: {
    color: 'text-brand-primary',
    title: t('misc.open'),
    state: 'open',
  },
  close: {
    color: 'text-critical-primary',
    title: t('misc.closed'),
    state: 'close',
  },
};

const ORDER_TYPES = { buy: 'buy', sell: 'sell' } as const;
const OFFLINE_ORDER_TYPE = 1;
const ALGORITHM_ORDER_TYPE = 2;

const OFFLINE_ORDER_START_HOUR = 18;

const ORDER_TYPES_LABEL = {
  offline: 'offline',
  online: 'online',
  conditional: 'conditional',
  SLTP: 'SLTP',
} as const;

const CONDITIONAL_CREATION_HOUR = 23;

const CONDITIONAL_ORDER_VALIDITY_TIME_PERIOD = [
  { label: t('misc.today'), value: 0 },
  { label: t('misc.tomorrow'), value: 1 },
  { label: t('misc.thirtyDays'), value: 30 },
  { label: t('misc.ninetyDays'), value: 90 },
];

const ORDERS_QUERY_KEYS = {
  trades: (orderId: string) => ['trades', orderId],
  tradesReport: (params: ComputedRef<string>) => ['trades-report', params],
  ordersHistoryReport: (params: ComputedRef<string>) => [
    'order-history-report',
    params,
  ],
  offlineOrderStatus: ['offline-status'],
  orderPosition: (orderId: string) => ['order-position', orderId],
  time: ['time'],
  order: (orderId: Ref<string | undefined>) => ['order', orderId],
  orders: ['orders'],
  ordersHistory: (queryParams: Ref<string>) => ['orders-history', queryParams],
  orderSummarize: ['order-summarize'],
  omsStatus: ['oms-status'],
};

const HEADLINE_TIME_PERIOD = {
  startHour: 8,
  startMinute: 0,
  endHour: 13,
  endMinute: 30,
};

const IRAN_TIME_ZONE_MILLI_SECONDS_OFFSET = 3.5 * 60 * 60 * 1000;

const MINIMUM_CONDITIONAL_TOTAL_VALUE = 10_000_000;

const ALGORITHM_RISK_AGREEMENT_ID = 33;

const ORDER_SOCKET_ERROR_DESCRIPTION = [
  'وضعیت نماد  اجازه این فعالیت را نمی دهد.',
  'وضعیت گروه  اجازه این فعالیت را نمی دهد.',
  'وضعیت نماد اجازه ثبت سفارش را نمی دهد',
  'ثبت سفارش برای نمادهای صندوق سکه، طلا و گواهی سکه قبل از ساعت 11:30 وجود ندارد',
  'امکان ثبت درخواست بعد از پایان زمان بازار وجود ندارد',
  'امکان ثبت سفارش در پایان روز کاری وجود ندارد',
  'ثبت درخواست قبل از شروع بازار امکان پذیر نیست',
  'محدودیت عدم ثبت سفارش در ساعت بعد از 15:00 ',
  'ثبت سفارش برای نمادهای صندوق سکه، طلا و گواهی سکه قبل از ساعت 11:30 وجود ندارد',
  'امکان ثبت سفارش در نمادهای غیر از صندوق درآمد ثابت فرابورسی و اخزا قبل از ساعت 8:45 وجود ندارد',
  'امکان ثبت درخواست در نمادهای غیر از صندوق درآمد ثابت فرابورسی و اخزا قبل از ساعت 8:45 وجود ندارد',
];

const CONDITIONAL_ORDER_CLIENT_TAG = 'Falcon-Conditional';

const SLTP_ORDER_CLIENT_TAG = 'Falcon-SLTP';

const MAXIMUM_SLTP_SELL_QUANTITY_PERCENT = 100;

const SLTP_QUANTITY_TYPE = {
  fixed: 0,
  investValue: 1,
  percent: 2,
};

const OFFLINE_ORDER_EXECUTION_TYPE = {
  desired: {
    next: 'Mobile-Next-Falcon-247-Algo',
    helium: 'Desktop-Helium-Falcon-247-Algo',
  },
  market: {
    next: 'Mobile-Next-Falcon-247M-Algo',
    helium: 'Desktop-Helium-Falcon-247M-Algo',
  },
};

const ZONE_ORDER_EXECUTION_TYPE = 'API-XON-Falcon-OTC-Algo';

export {
  ORDERS_LABEL,
  LEGACY_ORDER_STATUSES,
  LEGACY_ORDER_STATUSES_GROUPS,
  LEGACY_ORDER_STATUSES_LABEL,
  LEGACY_ORDER_PENDING_STATUSES,
  ORDER_FILTERS,
  ORDER_SIDES,
  ORDER_SIDES_LABEL,
  ORDER_SIDES_TABS,
  BASE_ORDER_FORM,
  VALIDITY_TYPES,
  EMPTY_ORDER_MESSAGE,
  STATUS,
  VALIDITY_TYPE_LABEL,
  IPO_REGISTERED_STATE_TITLE,
  SERVER_CLOCK_STATE,
  SERVER_CLOCK_STATE_DETAIL,
  ERRORS_TO_SHOW,
  ORDER_TYPES,
  OFFLINE_ORDER_TYPE,
  ALGORITHM_ORDER_TYPE,
  OFFLINE_ORDER_START_HOUR,
  ORDER_TYPES_LABEL,
  ORDERS_QUERY_KEYS,
  ALGORITHM_ORDER_TYPES,
  CONDITIONAL_CREATION_HOUR,
  CONDITIONAL_ORDER_VALIDITY_TIME_PERIOD,
  HEADLINE_TIME_PERIOD,
  IRAN_TIME_ZONE_MILLI_SECONDS_OFFSET,
  MINIMUM_CONDITIONAL_TOTAL_VALUE,
  ALGORITHM_RISK_AGREEMENT_ID,
  ORDER_SOCKET_ERROR_DESCRIPTION,
  CONDITIONAL_ORDER_CLIENT_TAG,
  NEW_OMS_ORDER_STATUSES,
  NEW_OMS_ORDER_STATUSES_GROUPS,
  NEW_OMS_ORDER_PENDING_STATUSES,
  NEW_OMS_ORDER_STATUSES_LABEL,
  NEW_OMS_ORDERS_HISTORY_CATEGORY,
  OFFLINE_ORDER_EXECUTION_TYPE,
  ALGORITHM_ORDER_CONDITIONS,
  ZONE_ORDER_EXECUTION_TYPE,
  SLTP_ORDER_CLIENT_TAG,
  MAXIMUM_SLTP_SELL_QUANTITY_PERCENT,
  SLTP_QUANTITY_TYPE,
};
